// DEPENDENCIES
import React, { useState, useEffect, useContext } from "react";

// CSS
import "./ProjectConsole.css";

// MSAL
import { useMsal } from "@azure/msal-react";

// API IMPORTS
import { getAPICall, postAPICall } from "../../../config/apiCalls";

// CONTEXT
import { UserContext } from "../../../context/UserContext";

// MUI COMPONENTS
import { Button, CircularProgress, IconButton } from "@mui/material";

// MUI ICONS
import EditIcon from "@mui/icons-material/Edit";

// CUSTOM COMPONENTS
import J2NoResultsFound from "../../Misc/J2NoResultsFound/J2NoResultsFound";
import ProjectConsoleEditAddDialog from "./ProjectConsoleEditAddDialog/ProjectConsoleEditAddDialog";
import TanStackTable from "../../Misc/Table/TanStackTable";

// CONSTANTS
import { convertToPST } from "../../../constants/dateTime";
import {
	checkboxSort,
	dateSort,
	defaultSort,
	notApplicable,
} from "../../Misc/Table/TanStackTableConstants";

// OTHER

// REACT COMPONENT
const ProjectConsole = (props) => {
	// MSAL
	const { instance, accounts } = useMsal();

	// STATES
	const [projects, setProjects] = useState([]);

	const [projectsRows, setProjectsRows] = useState([]);
	const [projectsColumns, setProjectsColumns] = useState([]);

	const [properties, setProperties] = useState([]);
	const [employees, setEmployees] = useState([]);
	const [buildingComponents, setBuildingComponents] = useState([]);

	const [recordToEdit, setRecordToEdit] = useState({});
	const [loading, setLoading] = useState(false);
	const [editAddDialogOpen, setEditAddDialogOpen] = useState(false);
	const [editing, setEditing] = useState(false);

	// CONTEXT
	const employeeID = useContext(UserContext);

	// USE EFFECT
	useEffect(() => {
		postAPICall(instance, accounts[0], "/api/logs/access/add", {
			page_name: "Project Console",
			employeeID: employeeID,
			notes: "Accessed the Project Console page",
		}).catch(() => {});
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		handleAPICalls();
	}, []);

	useEffect(() => {
		if (projects.length > 0 && buildingComponents.length > 0) {
			createColumnData();
			createRowData();
		}
	}, [projects, buildingComponents]);

	// INPUT HANDLERS

	const handleAddClick = () => {
		setEditing(false);
		setEditAddDialogOpen(true);
	};

	const handleEditClick = (record) => {
		setRecordToEdit(record);
		setEditing(true);
		setEditAddDialogOpen(true);
	};

	const handleEditAddDialogClose = () => {
		setEditing(false);
		setEditAddDialogOpen(false);
		setRecordToEdit({});
		handleAPICalls();
	};

	const handleEditAddDialogCloseNoRefresh = () => {
		setEditing(false);
		setEditAddDialogOpen(false);
		setRecordToEdit({});
	};

	// API FUNCTIONS

	const handleAPICalls = async () => {
		setLoading(true);
		await fetchProjects();
		await fetchProperties();
		await fetchEmployees();
		await fetchBuildingComponents();
		setLoading(false);
	};

	const fetchProjects = async (sortBy, sortDirection) => {
		let queryParams;
		if (sortBy && sortDirection) {
			queryParams = `sortBy=${sortBy}&sortDirection=${sortDirection}`;
		}

		// Fetch Projects
		try {
			const res = await getAPICall(
				instance,
				accounts[0],
				`/api/jobs/project/all?${queryParams}`
			);
			setProjects(res.data);
		} catch (error) {
			console.error(error);
		}
	};

	const fetchProperties = async () => {
		// Fetch Properties
		try {
			const res = await getAPICall(
				instance,
				accounts[0],
				"/api/jobs/property/all"
			);
			setProperties(res.data);
		} catch (error) {
			console.log(error);
		}
	};

	const fetchEmployees = async () => {
		// Fetch Employees
		try {
			const res = await getAPICall(
				instance,
				accounts[0],
				"/api/employees/all"
			);
			setEmployees(res.data);
		} catch (error) {
			console.log(error);
		}
	};

	const fetchBuildingComponents = async () => {
		try {
			const res = await getAPICall(
				instance,
				accounts[0],
				"/api/jobs/components/all"
			);
			setBuildingComponents(res.data);
		} catch (error) {
			console.error(error);
		}
	};

	// HELPER FUNCTIONS

	const createColumnData = () => {
		setProjectsColumns([
			{
				accessorKey: "property_name",
				header: () => <span>Property</span>,
				label: "Property",
				cell: ({ row }) => (
					<p>{row.original.property_name || notApplicable}</p>
				),
				filterType: "checkbox",
				filterFn: checkboxSort,
				filterOptions: properties.map((property) => ({
					label: property.name,
					value: property.name,
				})),
			},
			{
				accessorKey: "project_name",
				header: () => <span>Project Name</span>,
				label: "Project Name",
				cell: ({ row }) => (
					<p>{row.original.project_name || notApplicable}</p>
				),
				filterType: "checkbox",
				filterFn: checkboxSort,
				filterOptions: buildingComponents.map((component) => ({
					label: component.name,
					value: component.name,
				})),
			},
			{
				accessorKey: "display_name",
				header: () => <span>Display Name</span>,
				label: "Display Name",
				cell: ({ row }) => (
					<p>{row.original.display_name || notApplicable}</p>
				),
				filterType: "checkbox",
				filterFn: checkboxSort,
				filterOptions: buildingComponents.map((component) => ({
					label: component.abbreviated_name,
					value: component.abbreviated_name,
				})),
			},
			{
				accessorKey: "dropbox_path",
				header: () => <span>Dropbox File Path</span>,
				label: "Dropbox File Path",
				cell: ({ row }) => (
					<p>{row.original.dropbox_path || notApplicable}</p>
				),
				filterType: "textfield",
				filterFn: defaultSort,
			},
			{
				accessorKey: "owner",
				header: () => <span>Owner</span>,
				label: "Owner",
				cell: ({ row }) => <p>{row.original.owner || notApplicable}</p>,
				filterType: "textfield",
				filterFn: defaultSort,
			},
			{
				accessorKey: "created",
				header: () => <span>Created</span>,
				label: "Created",
				cell: ({ row }) => (
					<p>{row.original.created || notApplicable}</p>
				),
				filterType: "date",
				filterFn: dateSort,
			},
			{
				accessorKey: "edit",
				header: "",
				label: "Edit",
				cell: ({ row }) => (
					<IconButton
						onClick={() => handleEditClick(row.original.record)}
						disableRipple
						sx={{ justifyContent: "flex-end" }}
					>
						<EditIcon fontSize="small" />
					</IconButton>
				),
				enableColumnFilter: false,
				enableSorting: false,
			},
		]);
	};

	const createRowData = () => {
		setProjectsRows(
			projects.map((project) => ({
				property_name: project.property_name,
				project_name: project.name,
				display_name: project.display_name,
				dropbox_path: project.dropbox_path,
				owner:
					project.first_name && project.last_name
						? project.first_name + " " + project.last_name
						: null,
				created: convertToPST(project.created),
				record: project,
			}))
		);
	};

	// RENDER
	return (
		<div className="ProjectConsole">
			<div className="ProjectConsoleHeader">
				<div className="ProjectConsoleHeaderTitle">
					Project Console
					<Button
						variant="contained"
						color="primary"
						onClick={handleAddClick}
						disabled={loading}
					>
						New Project
					</Button>
				</div>
			</div>
			{loading && (
				<div className="loadingDiv">
					<CircularProgress color="primary" />
				</div>
			)}
			{!loading && (
				<div className="tableContainerDiv">
					{projects.length === 0 && <J2NoResultsFound />}
					{projects.length > 0 &&
						projectsRows.length > 0 &&
						projectsColumns.length > 0 && (
							<TanStackTable
								columnData={projectsColumns}
								rowData={projectsRows}
							/>
						)}
				</div>
			)}
			<ProjectConsoleEditAddDialog
				open={editAddDialogOpen}
				onClose={handleEditAddDialogClose}
				onCloseNoRefresh={handleEditAddDialogCloseNoRefresh}
				recordToEdit={recordToEdit}
				editing={editing}
				projects={projects}
				properties={properties}
				employees={employees}
				buildingComponents={buildingComponents}
			/>
		</div>
	);
};

// EXPORT
export default ProjectConsole;
